<template>
    <section class="order-card">
        <header class="header">
            <h1>Order Details</h1>
            <h2>#{{ order.id }} <em v-if="isShowOrderDate">Ordered {{ order.date | date }}</em></h2>
        </header>

        <article>
            <section class="section summary">
                <div class="summary__left">
                    <section class="detail ticket">
                        <h3 class="section__heading">Ticket Summary</h3>
                        <div
                            v-if="order.event.status === EVENT_STATUS.RESCHEDULED && [ORDER_STATUS.CREATED, ORDER_STATUS.PENDING, ORDER_STATUS.PROCESSED, ORDER_STATUS.SENT_TO_BUYER].includes(order.current_status)"
                            class="ticket__rescheduled-notification"
                        >
                            <span class="ticket__rescheduled-notification-icon icon-email-2"></span>
                            <span>For the most up-to-date event information please refer to your email</span>
                        </div>
                        <h4 class="ticket__event-name">{{ order.event.name }}</h4>
                        <p class="ticket__event">
                            <template v-if="[ORDER_STATUS.CREATED, ORDER_STATUS.PENDING, ORDER_STATUS.PROCESSED, ORDER_STATUS.SENT_TO_BUYER].includes(order.current_status)">
                                <Tag
                                    v-if="order.event.status && (order.event.status !== EVENT_STATUS.SCHEDULED)"
                                    class="ticket__event-status"
                                    context="status"
                                    :text="eventStatusCopy(order)"
                                />
                                <span v-if="[EVENT_STATUS.TBD, EVENT_STATUS.POSTPONED].includes(order.event.status)">Event updates will be emailed</span>
                                <template v-else>{{ order.event.local_date | date('ddd MMM D, YYYY') }} at {{ order.event.local_date | date('h:mma') }}</template>
                            </template>
                            <template v-else>{{ order.event.local_date | date('ddd MMM D, YYYY') }} at {{ order.event.local_date | date('h:mma') }}</template>
                            <br>
                            {{ order.event.venue.name }},
                            {{ cityAndStateInfo }}<!--
                            --><template v-if="order.event.venue.subdivision">, {{ order.event.venue.subdivision }}</template>
                        </p>
                    </section>

                    <section class="detail seat">
                        <div class="seat__details">
                            <div class="seat__detail">
                                <h4 class="detail__heading">Section</h4>
                                <p class="detail__body detail__body--seat">{{ order.ticket.section }}</p>
                            </div>
                            <div class="seat__detail">
                                <h4 class="detail__heading">Row</h4>
                                <p class="detail__body detail__body--seat">{{ order.ticket.row }}</p>
                            </div>
                            <div class="seat__detail">
                                <h4 class="detail__heading">Qty</h4>
                                <p class="detail__body detail__body--seat">{{ order.ticket.quantity }}</p>
                            </div>
                        </div>
                    </section>

                    <section
                        class="detail"
                        v-if="order.ticket.notes"
                    >
                        <h3 class="detail__heading">Seller Notes</h3>
                        <p class="detail__body">{{ order.ticket.notes }}</p>
                    </section>

                    <section
                        class="detail"
                        v-if="order.event.disclaimer"
                    >
                        <h3 class="detail__heading">Event Disclaimer</h3>
                        <p class="detail__body" v-html="order.event.disclaimer"></p>
                    </section>

                    <section class="inventory-supplier">
                        <img
                            class="inventory-supplier__logo"
                            :class="{ 'inventory-supplier__logo--primary': order.ticket.is_primary }"
                            :alt="order.ticket.is_primary ? $config.primaryInventorySupplier.checkout.alt : 'Powered by VividSeats'"
                            :src="order.ticket.is_primary ? `${$store.state.imagesCdn}/${$config.primaryInventorySupplier.checkout.staticImagePath}` : `${$store.state.imagesCdn}/powered_by_VS_V2.svg`"
                        >
                    </section>
                </div>
                <template v-if="config.isIntegrated">
                    <vsOrderCardDeliveryDetails :order="order" />
                </template>
                <template v-else>
                    <fxOrderCardDeliveryDetails :order="order" />
                </template>
            </section>

            <section
                class="section billing"
                v-if="!partnerIsPurchaser"
            >
                <h3 class="section__heading">Billing Summary</h3>

                <div class="billing__row">
                    <div
                        class="billing__col"
                        v-if="billingAddress"
                    >
                        <section class="detail">
                            <h4 class="detail__heading">Billing Address</h4>
                            <p class="detail__body">
                                {{ billingAddress.first_name }} {{ billingAddress.last_name }}
                                <br>
                                {{ billingAddress.address_line_1 }}
                                <template v-if="billingAddress.address_line_2">, {{ billingAddress.address_line_2 }}</template>
                                <template v-if="billingAddress.address_line_3">, {{ billingAddress.address_line_3 }}</template>
                                <br>
                                {{ billingAddress.city }}, {{ billingAddress.region }}
                                <br>
                                {{ billingAddress.postal_code }}
                            </p>
                        </section>
                        <section
                            class="detail"
                            v-if="billingAddress.phone_number"
                        >
                            <h4 class="detail__heading">Phone Number</h4>
                            <p class="detail__body">{{ billingAddress.phone_number }}</p>
                        </section>
                    </div>

                    <div
                        class="billing__col"
                        v-if="order.payment && order.payment.paid > 0"
                    >
                        <section class="detail">
                            <h4 class="detail__heading">Payment Method</h4>
                            <p
                                class="detail__body detail__body--logo"
                                v-if="creditCardType"
                            >
                                <img
                                    class="billing__logo billing__logo--credit-card"
                                    :alt="creditCardType.displayName"
                                    :src="`${$store.state.imagesCdn}${creditCardType.iconSlug}`"
                                >
                                {{ creditCardType.displayName }}
                            </p>
                            <p
                                class="detail__body"
                                v-else-if="payPalBuyerEmail"
                            >
                                <PaypalLogo
                                    class="billing__logo billing__logo--paypal"
                                />
                                <br>
                                <span class="billing__paypal-email">{{ payPalBuyerEmail }}</span>
                            </p>
                        </section>
                        <section class="detail">
                            <h4 class="detail__heading detail">Total Payment</h4>
                            <p class="detail__body">{{ order.payment.paid | numeral('$0,0.00') }} {{ order.payment.currency }}</p>
                        </section>
                    </div>

                    <div
                        class="billing__col"
                        v-if="isShowPointsEarned || isShowPointsRedeemed"
                    >
                        <section
                            class="detail"
                            v-if="isShowPointsRedeemed"
                        >
                            <h4 class="detail__heading">Rewards Redeemed</h4>
                            <p class="detail__body">{{ formatPoints(order.loyalty.paid, order.loyalty.currency) }}</p>
                        </section>
                        <section
                            class="detail"
                            v-if="isShowPointsEarned"
                        >
                            <h4 class="detail__heading">Rewards Earned</h4>
                            <p class="detail__body">{{ formatPoints(order.loyalty.earn, order.loyalty.currency) }}</p>
                        </section>
                    </div>
                </div>
            </section>
        </article>
    </section>
</template>

<script>
import config from 'config'
import { CREDIT_CARD_TYPES } from 'core/types/CreditCard'
import { EVENT_STATUS, ORDER_STATUS } from '../constants.js'
import { isDisplayInHandDate } from 'core/services/OrderService.js'
import vsOrderCardDeliveryDetails from '../components/VSOrderCardDeliveryDetails'
import fxOrderCardDeliveryDetails from '../components/FXOrderCardDeliveryDetails'
import DateUtil from 'core/utilities/DateUtil'
import loyaltyFormattable from '../mixins/LoyaltyFormattable'
import PaypalLogo from '../components/PayPalLogo.vue'
import Tag from 'core/components/TagComponent.vue'

const PAYPAL_PAYMENT_TYPE = 'PayPal'

export default {
    beforeCreate () {
        this.config = config
    },
    components: {
        PaypalLogo,
        Tag,
        vsOrderCardDeliveryDetails,
        fxOrderCardDeliveryDetails
    },
    computed: {
        billingAddress () {
            return ((this.order || {}).customer || {}).billing_address || null
        },
        isShowPointsEarned () {
            if (!(this.order && this.order.loyalty)) { return false }
            return Boolean(this.order.loyalty.earn && this.order.loyalty.currency)
        },
        isShowPointsRedeemed () {
            if (!(this.order && this.order.loyalty)) { return false }
            return Boolean(this.order.loyalty.paid && this.order.loyalty.currency)
        },
        cityAndStateInfo(){
            return this.order.event.venue.city_name || `${this.order.event.venue.city}, ${this.order.event.venue.state_code}`
        }

    },
    created () {
        this.EVENT_STATUS = EVENT_STATUS
        this.ORDER_STATUS = ORDER_STATUS

        if (this.order.payment && this.order.payment.cc_type) {
            this.creditCardType = Object.keys(CREDIT_CARD_TYPES).map(key => CREDIT_CARD_TYPES[key]).find(creditCard => {
                return creditCard.typeId.toUpperCase() === this.order.payment.cc_type.toUpperCase()
            })
        } else if (this.order.payment && this.order.payment.type === PAYPAL_PAYMENT_TYPE) {
            this.payPalBuyerEmail = this.order.payment.identification
        }

        this.partnerIsPurchaser = this.$config.isPurchaser || false
    },
    data () {
        return {
            creditCardType: null,
            payPalBuyerEmail: null
        }
    },
    filters: {
        date (value, format = 'MMM D, YYYY') {
            return DateUtil.format(value, format)
        }
    },
    methods: {
        eventStatusCopy (order) {
            if (order.event.status === EVENT_STATUS.TBD) { return 'Date TBD' }
            return order.event.status
        }
    },
    mixins: [
        loyaltyFormattable
    ],
    props: {
        isShowOrderDate: {
            default: false,
            required: false,
            type: Boolean
        },
        order: {
            required: true,
            type: Object
        }
    }
}
</script>

<style lang="postcss" scoped>
@import "theme";

.order-card {
    background-color: #fff;
    padding: 16px;

    @media (--mq-large-min) {
        padding: 24px;
    }
}

.billing {
    padding: 16px 0;

    & .billing__col {
        margin-bottom: 12px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    & .billing__logo {
        margin-right: 10px;

        &.billing__logo--credit-card {
            width: 25px;
        }

        &.billing__logo--paypal {
            width: 65px;
        }
    }

    & .billing__paypal-email {
        font-size: var(--fs-small);
        white-space: nowrap;
    }

    & .billing__row {
        display: flex;
        flex-direction: column;
    }

    @media (--mq-large-min) {
        padding: 24px 0;

        & .section__heading {
            margin: 0 0 16px !important;
        }

        & .billing__col {
            flex: 0 0 33.3%;
            margin-bottom: 0;
        }

        & .billing__row {
            flex-direction: row;
        }
    }
}

.detail {
    margin-bottom: 12px;

    &:last-child {
        margin-bottom: 0;
    }

    & .detail__body {
        color: #444649;
        font-size: var(--fs-normal);
        line-height: 18px;
        margin: 0;

        &.detail__body--logo {
            align-items: center;
            display: flex;
        }

        &.detail__body--seat {
            font-size: var(--fs-large);
            font-weight: 600;
        }
    }

    & .detail__heading {
        color: #808689;
        font-size: var(--fs-small);
        font-weight: 600;
        line-height: 17px;
        margin: 0 0 4px;
    }

    & .ticket-download {
        & .ticket-download__action {
            margin-top: 16px;
        }
    }

    @media (--mq-large-min) {
        margin-bottom: 24px;

        &:last-child {
            margin-bottom: 0;
        }

        & .detail__body {
            font-size: var(--fs-normal);

            &.detail__body--seat {
                font-size: var(--fs-larger);
                font-weight: var(--fw-normal);
            }
        }
    }
}

.header {
    padding-bottom: 16px;

    & h1 {
        color: #444649;
        font-size: var(--fs-largest);
        font-weight: var(--fw-medium);
        letter-spacing: 0.31px;
        line-height: 24px;
        margin: 0 0 4px;
    }

    & h2 {
        color: #6c7275;
        font-size: var(--fs-normal);
        font-weight: var(--fw-light);
        line-height: 17px;
        margin: 0;

        & em {
            font-size: var(--fs-small);
            margin-left: 8px;

            @media (--mq-large-min) {
                margin-left: 16px;
            }
        }
    }

    @media (--mq-large-min) {
        & h1 {
            margin-bottom: 8px;
            font-size: 24px;
        }
    }
}

.seat {
    & .seat__detail {
        margin-right: 24px;

        &:last-child {
            margin-right: 0;
        }
    }

    & .seat__details {
        align-items: top;
        display: flex;
    }
}

.section {
    border-top: 1px solid #cfd2d3;
    padding: 16px 0;

    &:last-child {
        padding-bottom: 0;
    }

    & .section__heading {
        color: #6c7275;
        font-size: var(--fs-large);
        font-weight: 600;
        line-height: 22px;
        margin: 0 0 16px;
    }

    @media (--mq-large-min) {
        padding: 24px 0;

        & .section__heading {
            margin: 0 0 10px;
        }
    }
}

.stock-type {
    & .stock-type__shipping {
        font-weight: bold;
        margin: 10px 0;
    }

    & .stock-type__icon {
        font-size: var(--fs-normal);
        left: 0;
        position: absolute;
        top: 2px;
    }

    & .stock-type__item {
        color: #444649;
        font-size: var(--fs-normal);
        font-weight: var(--fw-light);
        line-height: 18px;
        margin-bottom: 8px;
        padding-left: 28px;
        position: relative;

        &:last-child {
            margin-bottom: 0;
        }
    }

    @media (--mq-large-min) {
        & .stock-type__item {
            font-size: var(--fs-normal);
            margin-bottom: 16px;
        }
    }
}

.summary {
    display: flex;
    flex-direction: column;

    & .summary__left {
        order: 2;
        padding: 16px 0 0;
    }

    & .summary__right {
        border-bottom: 1px solid #cfd2d3;
        padding: 0 0 16px;
        order: 1;
    }

    @media (--mq-large-min) {
        flex-direction: row;

        & .summary__left {
            flex: 1 1 100%;
            order: 1;
            padding: 0 21px 0 0;
        }

        & .summary__right {
            border-bottom: 0;
            border-left: 1px solid #cfd2d3;
            flex: 0 0 40%;
            order: 2;
            margin-top: 0;
            padding: 0 0 0 21px;
        }
    }
}

.ticket {
    color: #444649;

    & .ticket__date {
        font-size: var(--fs-normal);
        line-height: 17px;
    }

    & .ticket__event {
        margin: 0 0 16px;
    }

    & .ticket__event-name {
        font-size: var(--fs-large);
        font-weight: 600;
        line-height: 22px;
        margin: 0 0 3px;
    }

    & .ticket__rescheduled-notification {
        align-items: center;
        background-color: rgba(248,204,1,0.2);
        display: flex;
        margin: 0 0 16px 0;
        padding: 10px 7px 8px 16px;

        @media (--mq-large-min) {
            margin: 6px 0 16px 0;
        }
    }

    & .ticket__rescheduled-notification-icon {
        font-size: 16px;
        padding-right: 16px;
    }

    & .ticket__event-status {
        margin-right: 8px;
    }

    @media (--mq-large-min) {
        & .ticket__event {
            font-size: var(--fs-normal);
            margin: 0 0 24px;
        }

        & .ticket__event-name {
            font-size: var(--fs-larger);
        }
    }
}

.tracking-information {
    margin-top: 10px;
    @media (--mq-large-min) {
        margin-top: 24px;
    }

    & .tracking-information__header {
        font-size: var(--fs-small);
        font-weight: var(--fw-bold);
        margin-bottom: 0;
    }

    & .tracking-information__tracking_number {
        margin: 5px 0 16px 0;
        @media (--mq-large-min) {
            margin: 5px 0;
        }
    }
}

.order__action {
    background-color: #008140;
    border-radius: 2px;
    color: white;
    cursor: pointer;
    display: inline-block;
    font-size: var(--fs-large);
    letter-spacing: 0.25px;
    line-height: 20px;
    padding: 6px 16px;
    text-align: center;
    text-decoration: none;

    &:hover {
        background: #007138;
    }

    &:active {
        background: #005128;
    }

    &:focus {
        outline: none;
    }
}

.inventory-supplier {
    & .inventory-supplier__logo {
        width: 100px;

        &.inventory-supplier__logo--primary {
            margin-left: 0px;
            width: 117px;
        }
    }
}
</style>
